import React, { useEffect, useMemo, useState } from "react";

import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import { useUpdateParameters } from "src/api/flowVersionQueries";
import { useAcquireVersionResourceLock } from "src/authoringMultiplayerLock/useVersionResourceLock";
import { Button } from "src/base-components/Button";
import { Parameter, ResourceType } from "src/clients/flow-api";
import { DeleteParameterModal } from "src/globalParameters/DeleteParameterModal";
import { EditParameterModal } from "src/globalParameters/EditParameterModal";
import { GlobalParametersCard } from "src/globalParameters/GlobalParametersCard";
import { refetchSchemaCompletionData } from "src/nodeEditor/api/queries";
import { AuthorPageParams } from "src/router/urls";
import { useNodeHighlighting } from "src/store/NodeHighlighting";
import { useGraphStore } from "src/store/StoreProvider";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type ParameterForm = Omit<Parameter, "value">;
export type ParametersForm = {
  params: ParameterForm[];
};

type Props = {
  params: Parameter[] | undefined;
  versionId: string;
  isReadonly: boolean;
};

export const ParametersList: React.FC<Props> = ({
  params,
  versionId,
  isReadonly,
}) => {
  const { version_id } = useParamsDecode<AuthorPageParams>();
  const [selectedParam, setSelectedParam] = useState<number | undefined>(
    undefined,
  );
  const [modalIsOpen, setModalIsOpen] = useState<
    "edit" | "editValue" | "delete" | "add" | undefined
  >(undefined);
  const [isDraggingParameter, setIsDraggingParameter] =
    useState<boolean>(false);
  const { lockedByOtherUser } = useAcquireVersionResourceLock(
    ResourceType.PARAMETER,
    version_id,
    Boolean(modalIsOpen || isDraggingParameter),
  );

  const formMethods = useForm<ParametersForm>({
    defaultValues: { params: params },
    values: params ? { params: params } : undefined,
  });
  const { clearHighlighting } = useNodeHighlighting();
  const { graphHasChanged } = useGraphStore();

  const isImmutable = isReadonly || lockedByOtherUser;

  const { fields, append, remove, move, update } = useFieldArray({
    control: formMethods.control,
    name: "params",
  });

  const { mutateAsync: mutateParameters } = useUpdateParameters(
    versionId,
    () => {
      graphHasChanged();
      refetchSchemaCompletionData(versionId);
    },
  );

  const submit = formMethods.handleSubmit(async (data) => {
    try {
      await mutateParameters(data.params);
    } catch (error) {
      formMethods.reset();
      throw error;
    }
  });

  // Also clear the node highlighting on the canvas when the component unmounts
  useEffect(() => {
    return clearHighlighting;
  }, [clearHighlighting]);

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDraggingParameter(false);
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const fieldToMove = fields.findIndex((field) => field.id === active.id);
      const fieldToInsert = fields.findIndex((field) => field.id === over.id);
      if (fieldToMove !== -1 && fieldToInsert !== -1) {
        move(fieldToMove, fieldToInsert);
        submit();
      }
    }
  };

  // This has to be memoized because otherwise the reference to the {} object changes on every render
  const selectedParamObject = useMemo(() => {
    return selectedParam !== undefined ? fields[selectedParam] : undefined;
  }, [selectedParam, fields]);

  return (
    <FormProvider {...formMethods}>
      {fields.length !== 0 && (
        <DndContext
          onDragEnd={handleDragEnd}
          onDragMove={() => setIsDraggingParameter(true)}
          onDragStart={() => setIsDraggingParameter(true)}
        >
          <SortableContext
            disabled={isImmutable}
            items={fields.map((param) => param.id)}
          >
            <div className="flex w-full flex-col items-center divide-y divide-gray-200 rounded border border-gray-200">
              {fields.map((param, index) => (
                <GlobalParametersCard
                  key={param.id}
                  id={param.id}
                  index={index}
                  parameter={param}
                  readonly={isImmutable}
                  onDelete={() => {
                    setSelectedParam(index);
                    setModalIsOpen("delete");
                  }}
                  onEdit={() => {
                    setSelectedParam(index);
                    setModalIsOpen("edit");
                  }}
                  onEditValue={() => {
                    setSelectedParam(index);
                    setModalIsOpen("editValue");
                  }}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <div className="mt-4 mb-8">
        <Button
          dataLoc="add-parameter-button"
          disabled={isImmutable}
          iconLeft={faPlus}
          size="base"
          variant="secondary"
          onClick={() => {
            setSelectedParam(undefined);
            setModalIsOpen("add");
          }}
        >
          Add Parameter
        </Button>
      </div>
      <DeleteParameterModal
        deleteParameter={async () => {
          remove(selectedParam);
          await submit();
        }}
        isOpen={modalIsOpen === "delete"}
        onClose={() => setModalIsOpen(undefined)}
      />
      <EditParameterModal
        currentParameterNames={fields.map((param) => param.name)}
        defaultValues={selectedParamObject}
        editParameter={async (newParameter) => {
          if (selectedParam !== undefined) {
            update(selectedParam, newParameter);
            await submit();
          } else {
            append(newParameter);
            await submit();
          }
        }}
        focusValueInput={modalIsOpen === "editValue"}
        isAdding={modalIsOpen === "add"}
        isOpen={
          modalIsOpen === "edit" ||
          modalIsOpen === "add" ||
          modalIsOpen === "editValue"
        }
        onClose={() => setModalIsOpen(undefined)}
      />
    </FormProvider>
  );
};
